import JSZip from "jszip";
import { MATTER_TEMPLATE_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  templateDictionaryItems,
  type TemplateDictionaryParent,
} from "@/lib/data/static/templateDictionaryItems";

type TemplateCategory = (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number];

const categoryByParent: Partial<
  Record<TemplateDictionaryParent, TemplateCategory>
> = {
  incidentReportSource: "Incident Report",
  propertyDamageAdjuster: "Property Damage",
  adjuster: "Insurance",
  medPayAdjuster: "Insurance",
  clientInsurance: "Insurance",
  defendantInsurance: "Insurance",
  clientVehicle: "Vehicle",
  defendantVehicle: "Vehicle",
  provider: "Medical Treatment",
  signer: "Signature Request",
  userSigner: "Signature Request",
};

export function inferTemplateCategories(
  variables: string[],
): TemplateCategory[] {
  const categories = new Set<TemplateCategory>();
  for (const variable of variables) {
    // Signer fields support numbered slots and custom field names.
    if (/^(UserSigner|Signer)\d*_[A-Za-z]+(?:_[^{}]+)?$/.test(variable)) {
      categories.add("Signature Request");
      continue;
    }
    const item = templateDictionaryItems.find(
      (item) =>
        variable === item.variable || variable.startsWith(`${item.variable}_`),
    );
    const category = item && categoryByParent[item.parent];
    if (category) categories.add(category);
  }
  return MATTER_TEMPLATE_CATEGORY_OPTIONS.filter((category) =>
    categories.has(category),
  );
}

export async function inferTemplateFileCategories(
  file: File,
): Promise<TemplateCategory[]> {
  if (!file.name.toLowerCase().endsWith(".docx")) return [];
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const variables: string[] = [];
  for (const entry of Object.values(zip.files)) {
    if (
      entry.dir ||
      !/^word\/(?:document|header\d*|footer\d*|footnotes|endnotes)\.xml$/.test(
        entry.name,
      )
    )
      continue;
    const document = new DOMParser().parseFromString(
      await entry.async("string"),
      "application/xml",
    );
    if (document.getElementsByTagName("parsererror").length)
      throw new Error("Invalid DOCX XML");
    for (const paragraph of Array.from(
      document.getElementsByTagNameNS("*", "p"),
    )) {
      const text = Array.from(paragraph.getElementsByTagNameNS("*", "t"))
        .map((node) => node.textContent ?? "")
        .join("");
      for (const match of text.matchAll(/\{\{([^{}]+)\}\}/g))
        variables.push(match[1].trim());
    }
  }
  return inferTemplateCategories(variables);
}
